import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 90000;

// QUEUED, PROCESSING and any status not listed here keep the poll going.
export const TERMINAL_STATUSES = [
  'SUCCEEDED',
  'DUPLICATE',
  'POSITION_CLOSED',
  'UNREADABLE_CV',
  'FAILED',
  'UNDELIVERABLE_EMAIL',
];

const getApplyStatus = (submissionId) =>
  fetch(
    `/.netlify/functions/careers-apply-status?id=${encodeURIComponent(submissionId)}`,
  )
    .then((response) => response.json())
    .catch(() => ({ ok: false }));

// submission is { submissionId } after a successful upload — submissionId is
// null when Rebecca gave none to poll — or null while the form is showing.
//
// Returns the outcome: { stage: 'processing' | 'timeout' } or
// { stage: 'done', status }, or null while the form is showing.
export const useApplyOutcome = (submission) => {
  const [settled, setSettled] = useState(null);

  useEffect(() => {
    if (!submission || !submission.submissionId) return undefined;
    const { submissionId } = submission;

    let ended = false;
    let nextPoll;

    const settle = (outcome) => {
      ended = true;
      clearTimeout(nextPoll);
      clearTimeout(deadline);
      setSettled({ submission, outcome });
    };

    // Its own timer rather than a poll count: each poll takes 2.5 s plus the
    // request, and a request that never returns must not hold the screen past
    // 90 s.
    const deadline = setTimeout(
      () => settle({ stage: 'timeout' }),
      POLL_TIMEOUT_MS,
    );

    const poll = () =>
      getApplyStatus(submissionId).then((result) => {
        if (ended) return;
        // A failed poll is ignored; only a terminal status or the deadline
        // ends the loop.
        if (result.ok && TERMINAL_STATUSES.indexOf(result.status) !== -1) {
          settle({ stage: 'done', status: result.status });
        } else {
          nextPoll = setTimeout(poll, POLL_INTERVAL_MS);
        }
      });
    poll();

    return () => {
      ended = true;
      clearTimeout(nextPoll);
      clearTimeout(deadline);
    };
  }, [submission]);

  if (!submission) return null;
  if (submission.submissionId === null) {
    return { stage: 'done', status: 'SUCCEEDED' };
  }
  return settled && settled.submission === submission
    ? settled.outcome
    : { stage: 'processing' };
};
